import type { PerspectiveCamera, Vector3 } from "three/webgpu";
import { distortWorldPosition, type GravitationalFieldProfile } from "../field/gravitationalField";
import { OpticalLensingProjector, type OpticalLensingProfile } from "../field/OpticalLensingProjector";

export type AuthoringWorldPosition = Vector3;
export type SemanticWorldPosition = Vector3;
export type VisualWorldPosition = Vector3;
export type RaymarchFieldPoint = readonly [number, number];

export class SpatialProjectionPipeline {
  private readonly opticalLensing = new OpticalLensingProjector();
  private accessibleMode = false;
  private visualFramePrepared = false;

  setAccessibleMode(enabled: boolean) {
    this.accessibleMode = enabled;
  }

  beginVisualFrame(
    camera: PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    singularityPosition: SemanticWorldPosition,
  ) {
    this.visualFramePrepared = !this.accessibleMode;
    if (this.visualFramePrepared) this.opticalLensing.prepareFrame(camera, viewportWidth, viewportHeight, singularityPosition);
  }

  endVisualFrame() {
    this.visualFramePrepared = false;
  }

  toSemantic(
    authoringPosition: AuthoringWorldPosition,
    target: SemanticWorldPosition,
    profile: GravitationalFieldProfile,
  ) {
    return distortWorldPosition(authoringPosition, target, profile);
  }

  toVisual(
    semanticPosition: SemanticWorldPosition,
    singularityPosition: SemanticWorldPosition,
    camera: PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    target: VisualWorldPosition,
    profile: OpticalLensingProfile,
  ) {
    if (this.accessibleMode) return target.copy(semanticPosition);
    if (this.visualFramePrepared)
      return this.opticalLensing.projectPrepared(semanticPosition, singularityPosition, target, profile);
    return this.opticalLensing.project(
      camera,
      viewportWidth,
      viewportHeight,
      semanticPosition,
      singularityPosition,
      target,
      profile,
    );
  }

  toVisualBlended(
    semanticPosition: SemanticWorldPosition,
    singularityPosition: SemanticWorldPosition,
    camera: PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    target: VisualWorldPosition,
    fromProfile: OpticalLensingProfile,
    toProfile: OpticalLensingProfile,
    blend: number,
  ) {
    if (this.accessibleMode) return target.copy(semanticPosition);
    if (this.visualFramePrepared)
      return this.opticalLensing.projectBlendedPrepared(
        semanticPosition,
        singularityPosition,
        target,
        fromProfile,
        toProfile,
        blend,
      );
    return this.opticalLensing.projectBlended(
      camera,
      viewportWidth,
      viewportHeight,
      semanticPosition,
      singularityPosition,
      target,
      fromProfile,
      toProfile,
      blend,
    );
  }
}
